import { Formik, FormikErrors, FormikTouched } from 'formik';
import moment from 'moment';
import { FunctionComponent, JSX, useCallback, useContext, useEffect, useState } from 'react';
import * as vp from 'vopay';
import * as Yup from 'yup';
import { Button } from 'vopay';
import { useAccountInfo } from '../../../providers/AccountInfo.provider';
import { useClientLogin } from '../../../providers/ClientLogin.provider';
import { useLockedFeatureDialogue } from '../../../providers/LockedFeatureDialogue.provider';
import { useLockedFeatures } from '../../../providers/LockedFeatures.provider';
import { usePendingData } from '../../../providers/PendingData.provider';
import AccountCreationContext from '../AccountCreation/AccountCreationContext';
import styles from './FundAccountForm.module.scss';
import { buildFundAccountRows } from './buildFundAccountRows.helper';
import { FundAccountHandler, FundRequest } from './types';
import { useTranslation } from 'react-i18next';

const { Box, Table, TextField, Select, Icon, Tooltip, Toggle, DatepickerSimple } = vp;

type FundAccountProps = {
  handlers: FundAccountHandler;
  clientAccount?: vp.ClientAccount;
  defaultTouches: FormikTouched<FundRequest>;
  defaultValues: FundRequest;
  showSubmitButton: boolean;
  isUnderReview: boolean;
  setIsUnderReview: (e: boolean) => void;
  hasBankAccount: boolean;
};

type StartDateType = {
  parent: {
    StartDate: string;
  };
};
const mandatoryFieldsByOrder = {
  Account: 0,
  ClientAccount: 1,
  Currency: 2,
  PaymentMethod: 3,
  Amount: 4,
  ScheduleType: 5,
  TakeFromAccountOn: 6,
  StartDate: 7,
  Frequency: 8,
  SemiMonthlyDates: 9,
  Ending: 10,
  EndDate: 11,
  EndingAfterPayments: 12,
  Description: 13,
  MessageForRecipient: 14,
  TransactionLabel: 15,
  Notes: 16,
};

type FC = FunctionComponent<FundAccountProps>;

const FundAccountForm: FC = ({
  handlers,
  clientAccount,
  defaultValues,
  defaultTouches,
  showSubmitButton,
  isUnderReview,
  setIsUnderReview,
  hasBankAccount,
}) => {
  const ctx = useContext(AccountCreationContext);
  const { setNewCurrencyInfo } = useAccountInfo();
  const [moreInfoNeeded, setMoreInfoNeeded] = useState(false);
  const fundAccountRow = buildFundAccountRows({
    clientAccount,
    moreInfoNeeded,
  });
  const [fieldErrorState, setFieldErrorState] = useState({
    Account: '',
    ClientAccount: '',
    Currency: '',
    PaymentMethod: '',
    Amount: '',
    ScheduleType: '',
    TakeFromAccountOn: '',
    StartDate: '',
    Frequency: '',
    SemiMonthlyDates: '',
    Ending: '',
    EndDate: '',
    EndingAfterPayments: '',
    Description: '',
    MessageForRecipient: '',
    TransactionLabel: '',
    Notes: '',
  });

  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.FundMyAccount.Form' });

  const handleFieldError = useCallback(
    (errorMessage: string | undefined, itemId: vp.FundAccountId, isDatePicker = false) => {
      if ((errorMessage && fieldErrorState[itemId]) || (errorMessage && isDatePicker)) {
        return errorMessage;
      }
      return null;
    },
    [fieldErrorState],
  );
  const handleFieldFocus = (itemId: vp.FundAccountId, errorMessages: FormikErrors<FundRequest>) => {
    Object.entries(mandatoryFieldsByOrder).forEach(([key, val]) => {
      if (mandatoryFieldsByOrder[itemId] >= val) {
        setFieldErrorState((prevState) => ({ ...prevState, [key]: errorMessages[key as vp.FundAccountId] || '' }));
      }
    });
  };
  const now = moment().format('YYYY-MM-DD');
  const { updateIsLocked } = useLockedFeatureDialogue();
  const lockedFeatures = useLockedFeatures();
  const {
    response: { ClientAccountsEnabled, Country },
  } = useClientLogin();
  const { initPendingData, updatePendingData } = usePendingData();

  const addMoreInformation = () => {
    setMoreInfoNeeded(true);
  };
  const [showOverallErrorMessage, setShowOverallErrorMessage] = useState<boolean>(false);
  return (
    <Formik
      initialValues={defaultValues}
      initialTouched={defaultTouches}
      onSubmit={handlers.onFundAccount}
      validationSchema={createdValidationSchema()}
      validateOnChange
      validateOnBlur
      enableReinitialize={vp.toBool(ClientAccountsEnabled)}
    >
      {({
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
        submitForm,
        setTouched,
        setValues,
        handleSubmit,
        isValid,
        validateForm,
      }) => {
        useEffect(() => {
          if (defaultValues.Account !== values.Account || values.PaymentMethod !== defaultValues.PaymentMethod) {
            setValues({ ...values, Account: '' });
            setTouched({ ...touched, Account: false });
          }
        }, [values.PaymentMethod]);
        useEffect(() => {
          initPendingData({ ...values, Amount: values.Amount?.toString() || '0' });
        }, []);
        useEffect(() => {
          updatePendingData({ ...values, Amount: values.Amount?.toString() || '0' });
        }, [values]);
        useEffect(() => {
          void validateForm();
        }, [defaultValues, defaultTouches]);
        useEffect(() => {
          if (
            defaultValues.Account !== values.Account ||
            values.PaymentMethod !== defaultValues.PaymentMethod ||
            values.Currency !== defaultValues.Currency
          ) {
            setTouched({ ...touched, Account: false });
          }
        }, [values.Currency]);

        useEffect(() => {
          if (defaultValues.Account !== '') {
            setValues({ ...values, Account: defaultValues.Account });
            ctx.handleFieldChange(vp.createSyntheticEvent('Account', defaultValues.Account));
          }
        }, [defaultValues.Account]);

        useEffect(() => {
          if (defaultValues.ClientAccount !== '') {
            setValues({ ...values, ClientAccount: defaultValues.ClientAccount });
            ctx.handleFieldChange(vp.createSyntheticEvent('ClientAccount', defaultValues.ClientAccount));
          }
        }, [values.ClientAccount]);
        ctx.handleFieldChange(vp.createSyntheticEvent('TransactionLabel', defaultValues.ClientAccount));

        useEffect(() => {
          handlers.onFundChange(values, errors, touched, submitForm, fundAccountRow);
        }, [values, errors]);

        const fundTable: vp.TableProps = {
          name: 'fund-account-fields',
          variant: 'pair',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: fundAccountRow
            .filter((x) => x.visible(values))
            .map(
              ({
                label,
                id,
                require,
                preset,
                type,
                placeholder,
                getSelectOptions,
                maxLength,
                isReadOnly,
                disableFiltering,
                searchCallback,
                SelectLabel,
                onChangeCallBack,
              }) => {
                let fieldComponent;

                const fieldLabel = (
                  <Box jc="fs" flx="col">
                    <Box ai="center">
                      {label} {getRequiredIcon(require)}
                      {id === 'TakeFromAccountOn' && (
                        <Tooltip
                          type="hover"
                          information={t('TakeFromAccountOn.toolTip')}
                          position="right"
                          hasArrow={false}
                          icon="question"
                        />
                      )}
                    </Box>
                  </Box>
                );

                if (type === 'singleDate') {
                  fieldComponent = (
                    <div className={styles.datePickerContainer}>
                      <DatepickerSimple
                        name={id}
                        value={String(values[id])}
                        error={handleFieldError(errors[id], id)}
                        onBlur={handleBlur}
                        onChange={(e) => {
                          if (e.target && e.target.value === 'undefined') {
                            e.target.value = String(values[id]);
                          }
                          handleChange(e);
                        }}
                        isDisabled={isUnderReview}
                      />
                    </div>
                  );
                } else if (type === 'input') {
                  fieldComponent = (
                    <TextField
                      preset={preset}
                      key={id}
                      name={id}
                      value={String(values[id])}
                      variant="standard"
                      placeholder={placeholder}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      maxLength={maxLength}
                      error={handleFieldError(errors[id], id)}
                      onFocus={() => handleFieldFocus(id, errors)}
                      disabled={isUnderReview}
                      readOnly={isReadOnly}
                    />
                  );
                } else if (type === 'select') {
                  fieldComponent = (
                    <Select
                      variant="standard"
                      options={getSelectOptions(values)}
                      lockedValues={lockedFeatures.LockedSelectOptions}
                      lockedCallback={(e: string) => updateIsLocked(true, e)}
                      key={id}
                      name={id}
                      value={String(values[id])}
                      placeholder={placeholder}
                      disabled={isUnderReview}
                      noOrdering
                      onBlur={handleBlur}
                      disableFiltering={disableFiltering ? disableFiltering(values) : undefined}
                      searchCallback={
                        disableFiltering ? (disableFiltering(values) ? searchCallback : undefined) : undefined
                      }
                      labelOverride={SelectLabel ? SelectLabel(values) : undefined}
                      onChange={(e) => {
                        if (onChangeCallBack) {
                          onChangeCallBack(e, values);
                        }
                        handleChange(e);
                        id === 'Currency' ? setNewCurrencyInfo(e.target.value) : handleChange(e);
                      }}
                      error={handleFieldError(errors[id], id)}
                      onFocus={() => handleFieldFocus(id, errors)}
                      isReadOnly={isReadOnly}
                    />
                  );
                } else {
                  fieldComponent = (
                    <Box width="100%" jc="fe">
                      <Toggle
                        name={id}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={Boolean(values[id])}
                        error={handleFieldError(errors[id], id)}
                        disabled={isUnderReview}
                      />
                    </Box>
                  );
                }
                return {
                  Field: fieldLabel,
                  Input: fieldComponent,
                };
              },
            ),
        };

        return (
          <>
            <Table {...fundTable} />
            {!moreInfoNeeded && (
              <div style={{ paddingRight: '1em' }}>
                <u>
                  <Button
                    noShadow
                    id="add-more-information-button"
                    label={t('Buttons.addMoreInformation')}
                    color="secondary"
                    onClick={() => {
                      addMoreInformation();
                    }}
                  />
                </u>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <Box mt="md" jc="fe" gap="sm" ai="center">
                {showOverallErrorMessage ? (
                  <div className={styles.overallErrorMessage}>{t('ErrorMessage.completeRequiredFields')}</div>
                ) : null}
                {isUnderReview ? (
                  <Button
                    id="Fund-back"
                    color="secondary"
                    label={t('Buttons.back')}
                    onClick={() => {
                      setIsUnderReview(false);
                    }}
                  />
                ) : null}
                <div
                  style={{ height: '41px' }}
                  onMouseEnter={() => {
                    if (!showSubmitButton || !isValid) {
                      setShowOverallErrorMessage(true);
                    }
                  }}
                  onMouseLeave={() => {
                    if (!showSubmitButton || !isValid) {
                      setShowOverallErrorMessage(false);
                    }
                  }}
                >
                  <Button
                    id="collection-submit"
                    isDisabled={!showSubmitButton || !isValid || !hasBankAccount}
                    type="submit"
                    label={isUnderReview ? t('Buttons.submit') : t('Buttons.review')}
                  />
                </div>
              </Box>
            </form>
          </>
        );
      }}
    </Formik>
  );
  function createdValidationSchema() {
    return Yup.object().shape({
      Currency: Yup.string().required(),
      PaymentMethod: Yup.string().required(),
      Account: Number(ClientAccountsEnabled) === 1 ? Yup.string().notRequired() : Yup.string().required(),
      ClientAccount: Number(ClientAccountsEnabled) === 0 ? Yup.string().notRequired() : Yup.string().required(),
      Amount: Yup.string()
        .test('amount-validation', t('ErrorMessage.amountMinValue'), (value) => {
          const amountValidation = Number(value) <= 0.0;
          return !amountValidation;
        })
        .required(),
      ScheduleType: Yup.string().when(['PaymentMethod', 'Currency'], {
        is: (PaymentMethod: string, Currency: string) =>
          PaymentMethod === 'Bank Account' &&
          ((Country === 'CA' && Currency === 'CAD') || (Country === 'US' && Currency === 'USD')),
        then: Yup.string().required(),
        otherwise: Yup.string(),
      }),
      TakeFromAccountOn: Yup.string().when(['PaymentMethod', 'ScheduleType', 'Currency'], {
        is: (PaymentMethod: string, ScheduleType: string, Currency: string) =>
          PaymentMethod === 'Bank Account' &&
          ScheduleType === 'single' &&
          ((Country === 'CA' && Currency === 'CAD') || (Country === 'US' && Currency === 'USD')),
        then: Yup.string()
          .test(
            'start-date-validation',
            t('ErrorMessage.takeFromAccountOnInvalidStartDate', { startDate: moment(now).format('YYYY-MM-DD') }),
            (value) => {
              const dateValidation = value !== 'undefined' && moment(Number(value)).format('YYYY-MM-DD') < now;
              return !dateValidation;
            },
          )
          .required(),
        otherwise: Yup.string(),
      }),
      StartDate: Yup.string().when(['PaymentMethod', 'ScheduleType', 'Currency'], {
        is: (PaymentMethod: string, ScheduleType: string, Currency: string) =>
          PaymentMethod === 'Bank Account' &&
          ScheduleType === 'recurring' &&
          ((Country === 'CA' && Currency === 'CAD') || (Country === 'US' && Currency === 'USD')),
        then: Yup.string()
          .test(
            'start-date-validation',
            t('ErrorMessage.invalidStartDate', { startDate: moment(now).add(1, 'days').format('YYYY-MM-DD') }),
            (value: string | undefined) => {
              const dateValidation =
                value !== 'undefined' &&
                moment(Number(value)).format('YYYY-MM-DD') < moment(now).add(1, 'days').format('YYYY-MM-DD');
              return !dateValidation;
            },
          )
          .required(),
        otherwise: Yup.string(),
      }),
      Frequency: Yup.string().when(['PaymentMethod', 'ScheduleType', 'Currency'], {
        is: (PaymentMethod: string, ScheduleType: string, Currency: string) =>
          PaymentMethod === 'Bank Account' &&
          ScheduleType === 'recurring' &&
          ((Country === 'CA' && Currency === 'CAD') || (Country === 'US' && Currency === 'USD')),
        then: Yup.string().required(),
        otherwise: Yup.string(),
      }),
      SemiMonthlyDates: Yup.string().when(['PaymentMethod', 'ScheduleType', 'Frequency', 'Currency'], {
        is: (PaymentMethod: string, ScheduleType: string, Frequency: string, Currency: string) =>
          PaymentMethod === 'Bank Account' &&
          ScheduleType === 'recurring' &&
          Frequency === 'semi-monthly' &&
          ((Country === 'CA' && Currency === 'CAD') || (Country === 'US' && Currency === 'USD')),
        then: Yup.string().required(),
        otherwise: Yup.string(),
      }),
      Ending: Yup.string().when(['PaymentMethod', 'ScheduleType', 'Currency'], {
        is: (PaymentMethod: string, ScheduleType: string, Currency: string) =>
          PaymentMethod === 'Bank Account' &&
          ScheduleType === 'recurring' &&
          ((Country === 'CA' && Currency === 'CAD') || (Country === 'US' && Currency === 'USD')),
        then: Yup.string().required(),
        otherwise: Yup.string(),
      }),
      EndDate: Yup.string().when(['PaymentMethod', 'ScheduleType', 'Ending', 'Currency'], {
        is: (PaymentMethod: string, ScheduleType: string, Ending: string, Currency: string) =>
          PaymentMethod === 'Bank Account' &&
          ScheduleType === 'recurring' &&
          Ending === 'End Date' &&
          Country === 'CA' &&
          Currency === 'CAD',
        then: Yup.string()
          .test(
            'end-date-validation',
            t('ErrorMessage.invalidEndDate'),
            (value: string | undefined, StartDate: StartDateType) => {
              const endDate = new Date(moment(Number(value)).format('YYYY-MM-DD'));
              const startDate = new Date(moment(Number(StartDate.parent.StartDate)).format('YYYY-MM-DD'));
              const dateValidation = endDate === startDate || endDate < startDate;
              return !dateValidation;
            },
          )
          .required(),
        otherwise: Yup.string(),
      }),
      EndingAfterPayments: Yup.string().when(['PaymentMethod', 'ScheduleType', 'Ending'], {
        is: (PaymentMethod: string, ScheduleType: string, Ending: string) =>
          PaymentMethod === 'Bank Account' && ScheduleType === 'recurring' && Ending === 'Payments',
        then: Yup.string().required(),
        otherwise: Yup.string(),
      }),
      Description: Yup.string().when(['PaymentMethod', 'ScheduleType', 'Currency', 'TakeFromAccountOn'], {
        is: (PaymentMethod: string, ScheduleType: string, Currency: string, TakeFromAccountOn: string) =>
          PaymentMethod === 'Bank Account' &&
          (ScheduleType === 'recurring' ||
            (TakeFromAccountOn && !vp.IsTodayDate(moment(Number(TakeFromAccountOn)).format('YYYY-MM-DD')))) &&
          Country === 'CA' &&
          Currency === 'CAD',
        then: Yup.string().required(),
        otherwise: Yup.string(),
      }),
      TransactionLabel: Yup.string().max(30, t('ErrorMessage.transactionLabelMaximumLength')),
    });
  }
  function getRequiredIcon(isRequired: boolean): JSX.Element {
    return isRequired ? <Icon icon="asterisk" size="xsm" color="error" /> : <></>;
  }
};

export default FundAccountForm;
